import { useState, useEffect, useRef } from 'react'
import { X, Upload, Download, Trash2, FileText, Image, FileSpreadsheet, FileArchive, File as FileIcon, AlertCircle } from 'lucide-react'
import api from '../../core/services/api'
import { Toast, ConfirmModal } from '../Toast'
import { AllegatoListItem } from '../../types/allegati'

interface AllegatiModalProps {
  isOpen: boolean
  onClose: () => void
  entityType: string
  entityId: string
  readonly?: boolean
}

export function AllegatiModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  readonly = false
}: AllegatiModalProps) {
  const [allegati, setAllegati] = useState<AllegatoListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [titolo, setTitolo] = useState('')
  const [note, setNote] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      loadAllegati()
    }
  }, [isOpen, entityType, entityId])

  const loadAllegati = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/v1/allegati/${entityType}/${entityId}`)
      setAllegati(res.data)
    } catch {
      setToast({ message: 'Errore nel caricamento allegati', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile || !titolo.trim()) {
      setToast({ message: 'Seleziona un file e inserisci un titolo', type: 'error' })
      return
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      setToast({ message: 'File troppo grande (max 10 MB)', type: 'error' })
      return
    }

    try {
      setUploading(true)
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('titolo', titolo.trim())
      if (note.trim()) formData.append('note', note.trim())

      await api.post(`/v1/allegati/${entityType}/${entityId}`, formData, {
        headers: { 'Content-Type': undefined }
      })

      setToast({ message: 'Allegato caricato', type: 'success' })
      setSelectedFile(null)
      setTitolo('')
      setNote('')
      if (fileInputRef.current) fileInputRef.current.value = ''
      loadAllegati()
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Errore nel caricamento'
      setToast({ message: msg, type: 'error' })
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async (allegato: AllegatoListItem) => {
    try {
      const res = await api.get(`/v1/allegati/download/${allegato.id}`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', allegato.filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      setToast({ message: 'Errore nel download', type: 'error' })
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await api.delete(`/v1/allegati/${deleteTarget}`)
      setToast({ message: 'Allegato eliminato', type: 'success' })
      setDeleteTarget(null)
      loadAllegati()
    } catch {
      setToast({ message: "Errore nell'eliminazione", type: 'error' })
      setDeleteTarget(null)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    })
  }

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return <Image className="w-8 h-8 text-green-500" />
    if (contentType.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />
    if (contentType.includes('word')) return <FileText className="w-8 h-8 text-blue-500" />
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return <FileSpreadsheet className="w-8 h-8 text-green-600" />
    if (contentType.includes('zip')) return <FileArchive className="w-8 h-8 text-yellow-600" />
    return <FileIcon className="w-8 h-8 text-gray-500" />
  }

  if (!isOpen) return null

  return (
    <>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Elimina Allegato"
        message="Sei sicuro di voler eliminare questo allegato? L'operazione non e' reversibile."
        confirmText="Elimina"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        variant="danger"
      />

      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Allegati ({allegati.length})
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Upload Form */}
            {!readonly && (
              <div className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Carica Nuovo Allegato
                </h3>

                <div className="space-y-3">
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
                    >
                      Scegli file
                    </button>
                    <span className="ml-3 text-sm text-gray-500">
                      {selectedFile ? `${selectedFile.name} (${formatFileSize(selectedFile.size)})` : 'Nessun file selezionato'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Titolo *</label>
                      <input
                        type="text"
                        value={titolo}
                        onChange={(e) => setTitolo(e.target.value)}
                        placeholder="es. Contratto firmato"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Note</label>
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Note opzionali..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleUpload}
                    disabled={uploading || !selectedFile || !titolo.trim()}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Caricamento...' : 'Carica'}
                  </button>
                </div>
              </div>
            )}

            {/* Lista Allegati */}
            {loading ? (
              <div className="text-center py-8 text-gray-500">Caricamento...</div>
            ) : allegati.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <FileIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessun allegato presente</p>
              </div>
            ) : (
              <div className="space-y-2">
                {allegati.map((allegato) => (
                  <div
                    key={allegato.id}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-shrink-0">
                      {getFileIcon(allegato.content_type)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm truncate">
                        {allegato.titolo}
                      </div>
                      <div className="text-xs text-gray-500">
                        {allegato.filename} · {formatFileSize(allegato.file_size)} · {formatDate(allegato.created_at)}
                      </div>
                      {allegato.note && (
                        <div className="text-xs text-gray-600 mt-0.5 truncate">
                          {allegato.note}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleDownload(allegato)}
                        className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Scarica"
                      >
                        <Download className="w-4 h-4" />
                      </button>

                      {!readonly && (
                        <button
                          onClick={() => setDeleteTarget(allegato.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Info */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                Max 10 MB per file. Formati: PDF, immagini (JPG, PNG, GIF), documenti Office, TXT, ZIP.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
